import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/LanguageContext";
import { loadMetaPixel, removeMetaPixelCookies } from "./metaPixel";
import { loadGoogleAnalytics, removeGoogleAnalyticsCookies } from "./googleAnalytics";

const STORAGE_KEY = "qapex_cookie_consent";
// Incrementare se cambiano i cookie usati: la scelta salvata viene invalidata e il banner ricompare
const CONSENT_VERSION = 2; // 2: aggiunto Google Analytics
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 mesi, come indicato nella Cookie Policy

const readConsent = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!saved || saved.version !== CONSENT_VERSION) return null;
    if (Date.now() - saved.date > CONSENT_MAX_AGE_MS) return null;
    return saved;
  } catch {
    return null;
  }
};

const CookieConsentContext = createContext(null);

export const CookieConsentProvider = ({ children }) => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(readConsent);
  const [bannerOpen, setBannerOpen] = useState(() => consent === null);
  // Pannello "Scopri di più e personalizza" con il toggle dei cookie di marketing
  const [customizing, setCustomizing] = useState(false);
  const [marketingDraft, setMarketingDraft] = useState(false);

  useEffect(() => {
    if (consent?.marketing) {
      loadMetaPixel();
      loadGoogleAnalytics();
    }
  }, [consent]);

  const choose = (marketing) => {
    const next = { version: CONSENT_VERSION, marketing, date: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // storage non disponibile: la scelta vale solo per questa visita
    }
    setBannerOpen(false);
    setCustomizing(false);

    // Pixel e Analytics non si possono scaricare a pagina aperta: alla revoca puliamo i cookie e ricarichiamo
    if (!marketing && (window.fbq || window.gtag)) {
      removeMetaPixelCookies();
      removeGoogleAnalyticsCookies();
      window.location.reload();
      return;
    }
    setConsent(next);
  };

  const value = useMemo(
    () => ({
      openPreferences: () => {
        setMarketingDraft(Boolean(consent?.marketing));
        setBannerOpen(true);
      },
    }),
    [consent]
  );

  const pill = "px-8 py-2 rounded-full font-bold text-sm transition-opacity hover:opacity-90";

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      {bannerOpen && (
        // Grafica ripresa dal banner iubenda usato in precedenza
        <div
          role="dialog"
          aria-live="polite"
          aria-label={t("legal.banner.settings")}
          className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          <div className="max-w-5xl mx-auto bg-white text-[#252525] rounded-md shadow-2xl px-4 py-5 sm:px-4 sm:py-6 text-sm leading-relaxed text-left max-h-[80vh] overflow-y-auto">
            <p>
              {t.rich("legal.banner.text", {
                b: (chunk) => <b>{chunk}</b>,
                link: (chunk) => (
                  <Link to="/cookie-policy" className="underline" onClick={() => setBannerOpen(false)}>
                    {chunk}
                  </Link>
                ),
              })}
            </p>
            <p>{t("legal.banner.text2")}</p>
            <p>{t("legal.banner.text3")}</p>

            {customizing && (
              <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
                <div className="py-3 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-bold">{t("legal.banner.necessaryTitle")}</p>
                    <p className="text-gray-600">{t("legal.banner.necessaryText")}</p>
                  </div>
                  <span className="shrink-0 text-xs font-bold text-[#0073CE]">{t("legal.banner.alwaysOn")}</span>
                </div>
                <label className="py-3 flex items-start justify-between gap-4 cursor-pointer">
                  <div>
                    <p className="font-bold">{t("legal.banner.marketingTitle")}</p>
                    <p className="text-gray-600">{t("legal.banner.marketingText")}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={marketingDraft}
                    onChange={(e) => setMarketingDraft(e.target.checked)}
                    className="shrink-0 mt-1 w-5 h-5 accent-[#0073CE]"
                  />
                </label>
              </div>
            )}

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center gap-3">
              {customizing ? (
                <button onClick={() => choose(marketingDraft)} className={`${pill} bg-[#DADADA] text-[#4D4D4D]`}>
                  {t("legal.banner.save")}
                </button>
              ) : (
                <button onClick={() => setCustomizing(true)} className={`${pill} bg-[#DADADA] text-[#4D4D4D]`}>
                  {t("legal.banner.customize")}
                </button>
              )}
              <div className="flex gap-3 sm:ml-auto">
                <button onClick={() => choose(false)} className={`${pill} flex-1 bg-[#0073CE] text-white`}>
                  {t("legal.banner.reject")}
                </button>
                <button onClick={() => choose(true)} className={`${pill} flex-1 bg-[#0073CE] text-white`}>
                  {t("legal.banner.accept")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => useContext(CookieConsentContext);
